import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Head from 'next/head';
import get from 'lodash.get';

import { setTriggerTop as setTriggerTopAction } from 'redux/modules/layout';
import loadScript from 'lib/loadScript';
import HeadlinePropType from 'lib/CustomPropTypes/headline';

import MiniPlayerTease from 'components/MiniPlayerTease';

import { HeaderFallback } from './Fallback';
import { HeaderNavbar } from './Navbar';
import { Sponsor } from './Sponsor';

const mapStateToProps = ({
  navbar,
  article,
  shared,
}) => ({
  headline: navbar.headline,
  isNativeArticle: shared.isNativeArticle,
  shareUrl: navbar.shareUrl,
  sponsorAdEnabled: get(article, ['content', 0, 'adsEnabled'], true),
});

const mapActionsToProps = {
  setTriggerTop: setTriggerTopAction,
};

class ServiceHeader extends React.Component {
  static propTypes = {
    header: PropTypes.shape({
      css: PropTypes.shape({}),
      js: PropTypes.shape({
        src: PropTypes.arrayOf(
          PropTypes.string,
        ),
      }),
      html: PropTypes.arrayOf(
        PropTypes.string,
      ),
    }).isRequired,
    shareUrl: PropTypes.string,
    headline: HeadlinePropType,
    setTriggerTop: PropTypes.func.isRequired,
    isNativeArticle: PropTypes.bool,
    sponsorAdEnabled: PropTypes.bool,
    vertical: PropTypes.string.isRequired,
  };

  static defaultProps = {
    isNativeArticle: false,
    shareUrl: null,
    sponsorAdEnabled: true,
    headline: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      ready: false,
    };
  }

  componentDidMount() {
    if (this.hasHeader() && window) {
      const {
        header,
        headline,
        shareUrl,
        vertical,
      } = this.props;

      const {
        js: {
          src = [],
        },
      } = header;

      // Set brand/vertical in config object
      if (typeof window.HFSconfig === 'undefined') {
        window.HFSapi = {};
        window.HFSconfig = {
          brand: vertical,
          header: {
            primary: get(headline, 'primary'),
            social: get(headline, 'social'),
            url: this.getSocialShareUrl(shareUrl),
          },
        };
      }

      // Attach scripts
      src.forEach((script, index) => {
        loadScript(script)
          .then(() => {
            // Fire window event to initialize header
            if (index + 1 >= src.length) {
              const event = new CustomEvent('HFS.ready', { detail: 'header' });
              window.dispatchEvent(event);
              // Set ready
              this.setState({ ready: true });
            }
          });
      });

      // Observe height changes
      window.addEventListener('HFS.recalculateHeight', this.recalculateHeight);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('HFS.recalculateHeight', this.recalculateHeight);
  }

  getSocialShareUrl = (path) => {
    const { vertical } = this.props;
    if (/^\//.test(path)) {
      switch (vertical) {
        case 'today':
          return `https://www.today.com${path}`;
        case 'msnbc':
          return `https://www.msnbc.com${path}`;
        case 'noticias':
          return `https://www.telemundo.com${path}`;
        default:
          return `https://www.nbcnews.com${path}`;
      }
    }
    return path;
  }

  recalculateHeight = () => {
    if (this.wrapperRef && this.wrapperRef.getBoundingClientRect) {
      const { setTriggerTop } = this.props;
      const { top } = this.wrapperRef.getBoundingClientRect();
      setTriggerTop(top + window.pageYOffset);
    }
  }

  hasHeader = () => {
    const { header } = this.props;
    return header && header.html && header.html.length;
  }

  onGlobalInsertShow = () => {
    if (window && window.HFSapi && typeof window.HFSapi.enableGlobalInsert === 'function') {
      window.HFSapi.enableGlobalInsert();
    }
  }

  insertMiniTease = () => {
    const { ready } = this.state;
    if (ready && this.wrapperRef && this.wrapperRef.querySelector) {
      const container = this.wrapperRef.querySelector('.js-global-nav-insert');
      if (container) {
        return ReactDOM.createPortal(
          <MiniPlayerTease
            onShowTease={this.onGlobalInsertShow}
          />,
          container,
        );
      }
    }
    return null;
  }

  insertSponsorAd = () => {
    const { ready } = this.state;
    const {
      isNativeArticle,
      sponsorAdEnabled,
      vertical,
    } = this.props;
    if (ready && this.wrapperRef && this.wrapperRef.querySelector && !isNativeArticle && sponsorAdEnabled && vertical !== 'sponsoredcontent') {
      const container = this.wrapperRef.querySelector('.js-sponsor-wrap');
      if (container) {
        return ReactDOM.createPortal(
          <Sponsor />,
          container,
        );
      }
    }
    return null;
  }

  // TODO (BENTO-13916): this is brittle, extract in new HFS configuration option
  getProcessedMarkup = (html) => {
    const navHTML = html.match(/<nav\s[^>]+>((?!<\/nav>).*)<\/nav>/);
    if (Array.isArray(navHTML) && navHTML.length >= 2) {
      return navHTML[1];
    }
    return html;
  }

  // TODO (BENTO-13916): this is brittle, extract in new HFS configuration option
  hasConfiguredNavClass = (html, className) => {
    const matchClasses = html.match(/<nav\sclass="([^"]+)"/);
    if (Array.isArray(matchClasses) && matchClasses.length >= 2) {
      return matchClasses[1].split(' ').includes(className);
    }
    return false;
  }

  render() {
    if (!this.hasHeader()) {
      return <HeaderFallback />;
    }

    const {
      header: {
        css: { link: links },
        js: { html: scripts },
        html: [html],
      } = {},
    } = this.props;

    return (
      <>
        <Head>
          {/* Attach stylesheets */}
          {Array.isArray(links) && links.length && links.map((href) => (
            <link key={href} href={href} rel="stylesheet" />
          ))}

          {/* Attach inline scripts */}
          {scripts && Array.isArray(scripts) && scripts.map((src) => (
            <script
              key={src.replace(/\s+/g, '').substr(0, 10)}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: decodeURIComponent(src) }}
            />
          ))}
        </Head>

        <div
          className="layout-header"
          id="hfs-header"
          ref={(el) => { this.wrapperRef = el; }}
        >
          <HeaderNavbar
            html={this.getProcessedMarkup(html)}
            showGlobal={this.hasConfiguredNavClass(html, 'show-global')}
          />
          <div className="hfsh-spacer" />

          {/* Render tease portal */}
          {this.insertMiniTease()}

          {/* Render sponsorAd portal */}
          {this.insertSponsorAd()}
        </div>
      </>
    );
  }
}

export default connect(mapStateToProps, mapActionsToProps)(
  ServiceHeader,
);
